import { Injectable } from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormArray,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import _ from 'lodash';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { debounceTime, first, map, pairwise, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { UserPlanning } from 'src/app/classes/user-planning';
import { environment } from '../../environments/environment';
import { Entity } from '../classes/entity.class';
import { PlanningFile } from '../classes/planning-file';
import { PlanningHasEntity } from '../classes/planning-has-entity.class';
import { PlanningMaterial } from '../classes/planning-material.class';
import { Planning } from '../classes/planning.class';
import { Status } from '../classes/status.class';
import { User } from '../classes/user.class';
import { LocalStorage } from '../storage.class';
import { Utils } from '../utils.class';
import { localEmployeeAvailableValidator } from '../validators/local-employee-available.validator';
import { localEntityIsAvailableValidator } from '../validators/local-entity-is-available.validator';
import { localUserIsAvailableValidator } from '../validators/local-user-is-available.validator';
import { ApiService } from './api/api.service';
import { AuthorisationService } from './auth/authorisation.service';
import { EmployeeService } from './employee.service';
import { EntitiesService } from './entities/entities.service';
import { EntityField, EntityType } from './entities/entity-type.class';
import { LocationService } from './location.service';
import { PlanningFixedService } from './planning/planning-fixed.service';
import { PlanningHasService } from './planning/planning-has.service';
import { StatusService } from './planning/status.service';
import { UserPlanningService } from './planning/user-planning.service';
import { UserService } from './user/user.service';

@Injectable({
    providedIn: 'root',
})
export class EntityTypeService {
    constructor(
        private apiService: ApiService,
        private statusService: StatusService,
        private planningFixedService: PlanningFixedService,
        private userPlanningService: UserPlanningService,
        private planningHasService: PlanningHasService,
        private entitiesService: EntitiesService,
        private userService: UserService,
        private employeeService: EmployeeService,
        private locationService: LocationService
    ) {}

    writeEntityTypesToDb() {
        if (!environment.production) {
            this.apiService.postCall$<any>('entitytypes/import', { entityTypes: environment.entitytypes }).subscribe();
        }
    }

    getTableHeaders(entityType: EntityType): EntityField[] {
        const headers = [];

        [entityType.fields_phe, entityType.fields_phe_data].forEach(entityFields => {
            Object.entries(entityFields).forEach(([fcKey, entityField]) => {
                entityField.key = fcKey;
                headers.push(entityField);
            });
        });

        return headers.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    getFormFromEntityType(entityType: EntityType, entity: Entity, subscriptions: Subscription): PlanningFormData {
        const planningForm = this.createPlanningFormGroup(entityType);
        const formData = new PlanningFormData();
        formData.form = planningForm.form;
        formData.planningFieldsMap = planningForm.planningFieldsMap;
        formData.planningFiles = [];
        formData.mainEntityType = entityType;
        formData.mainEntity = entity;
        this.addPheToForm(formData, entityType, entity, true, undefined, subscriptions);

        this.watchPerformerFields(subscriptions, formData, entityType);
        this.watchStatusForDefaultEmployees(subscriptions, formData, entityType);
        this.applyDefaultEmployees(formData, entityType, subscriptions);
        return this.buildConditionalValidators(formData, subscriptions);
    }

    getFormFromPlanning(planning: Planning, subscriptions: Subscription, copy = false, entityTypeKey = null) {
        const mainEntityType = environment.entitytypes[entityTypeKey ?? planning.mainPlanning.entitytype_key];
        if (copy && entityTypeKey) {
            planning = structuredClone(planning);
            planning.mainPlanning.entitytype_key = entityTypeKey;
            planning.planning_has.find(ph => ph.id === planning.planning_has_id).entitytype_key = entityTypeKey;
        }
        const planningForm = this.createPlanningFormGroup(mainEntityType, planning);

        const formData = new PlanningFormData();

        formData.mainEntityType = mainEntityType;
        formData.mainEntity = planning.mainPlanning.entity;
        formData.orginal = copy ? null : _.cloneDeep(planning);
        if (copy) {
            formData.orginal = new Planning();
            formData.orginal.user_planning = [];

            planning.user_planning.forEach(up => {
                const cup = { ...up };

                delete cup.id;
                delete cup.updated_at;

                formData.orginal.user_planning.push(cup);
            });

            formData.orginal.planning_materials = (planning.planning_materials ?? []).map(pm => {
                const cpm = _.cloneDeep(pm);

                delete cpm.id;
                delete cpm.planning_id;
                delete cpm.updated_at;

                return cpm;
            });
        }

        formData.form = planningForm.form;
        formData.planningFieldsMap = planningForm.planningFieldsMap;
        formData.planningFiles = [
            ...(planning.planning_files.map(pf => {
                if (copy) {
                    pf = structuredClone(pf);
                    delete pf.id;
                    return pf;
                }
                return pf;
            }) ?? []),
        ];

        if (mainEntityType.fields_planning) {
            Object.entries(mainEntityType.fields_planning).forEach(([key, entityField]) => {
                if (planning[key] !== undefined) {
                    formData.form.controls.planning.get(key).setValue(planning[key]);
                }

                entityField.extraFields?.forEach(hiddenField => {
                    formData.form.controls.planning.get(hiddenField).setValue(planning[hiddenField]);
                });
            });
        }

        if (mainEntityType.fields_planning_data) {
            Object.entries(mainEntityType.fields_planning_data).forEach(([key]) => {
                formData.form.controls.planningData.get(key).setValue(planning.data ? planning.data[key] : null);
            });
        }

        planning.planning_has.forEach(phe => {
            const entityType = environment.entitytypes[phe.entitytype_key];
            const pheForm = this.addPheToForm(
                formData,
                entityType,
                planning.mainPlanning.entity,
                planning.planning_has_id === phe.id,
                phe.id,
                subscriptions
            );

            pheForm.pheFields.id = phe.id;
            pheForm.pheFields.orginal = copy ? null : phe;
            pheForm.pheFields.form = pheForm.form;

            if (entityType.fields_phe) {
                Object.entries(entityType.fields_phe).forEach(([key, entityField]) => {
                    pheForm.form
                        .get('phe')
                        .get(key)
                        .setValue(phe[key] ?? null);

                    entityField.extraFields?.forEach(hiddenField => {
                        pheForm.form.get('phe').get(hiddenField).setValue(phe[hiddenField]);
                    });
                });
            }

            if (entityType.fields_phe_data) {
                const pheData = phe.data ?? {};

                Object.entries(entityType.fields_phe_data).forEach(([key]) => {
                    pheForm.form
                        .get('pheData')
                        .get(key)
                        .setValue(pheData[key] ?? null);
                });
            }
        });

        this.watchPerformerFields(subscriptions, formData, mainEntityType);
        this.watchStatusForDefaultEmployees(subscriptions, formData, mainEntityType);

        return this.buildConditionalValidators(formData, subscriptions);
    }

    getPlanningObject(planningFormData: PlanningFormData) {
        let planning = planningFormData.orginal ? structuredClone(planningFormData.orginal) : new Planning();

        Object.assign(planning, planningFormData.form.value.planning);

        planning.data = {};

        Object.assign(planning.data, planningFormData.form.value.planningData);

        if (planning.data.execute_after_date instanceof Date) {
            // Als plain yyyy-MM-dd (lokale kalenderdatum) versturen i.p.v. een volledige Date: anders
            // serialiseert HttpClient 'm via Date.toJSON() naar een UTC-instant, wat een datum-only veld
            // afhankelijk maakt van tijdstip/tijdzone i.p.v. gewoon een kalenderdatum.
            planning.data.execute_after_date = Utils.formatDate(planning.data.execute_after_date);
        }

        const planning_has = [];

        planningFormData.pheFields.forEach(pheData => {
            const phe = planning.planning_has?.find(p => p.id === pheData.id && p.id) ?? new PlanningHasEntity();

            Object.assign(phe, pheData.form.value.phe);

            phe.data = {};

            Object.assign(phe.data, pheData.form.value.pheData);

            planning_has.push(phe);
        });

        planning.planning_has = planning_has;

        planning.user_planning = planningFormData.form.controls.employeePlanning.value as UserPlanning[];
        planning.planning_materials = planningFormData.form.controls.planningMaterials.value as PlanningMaterial[];

        // Process planning materials if present
        if (planningFormData.form.contains('planningMaterials')) {
            const materialsFormArray = planningFormData.form.get('planningMaterials') as any as FormArray;

            planning.planning_materials = materialsFormArray.value.map(
                (pm: PlanningMaterial & { raw_material_ids: number[] }) => ({
                    id: pm.id,
                    material_id: pm.material_id,
                    raw_material_ids: pm.raw_material_ids,
                    quantity: pm.quantity,
                })
            );
        }

        planning.planning_files = [...(planningFormData.planningFiles ?? [])];

        return planning;
    }

    addPheToForm(
        formData: PlanningFormData,
        entityType: EntityType,
        mainEntity?: Entity,
        isMainPlanning = false,
        ownId?: number,
        subscriptions?: Subscription
    ) {
        const pheForm = this.createPheFormGroup(entityType, formData?.orginal);

        if (!pheForm.form.controls.phe.get('entitytype_key')) {
            pheForm.form.controls.phe.addControl('entitytype_key', new FormControl(entityType.key));

            if (isMainPlanning) {
                pheForm.form.controls.phe.addControl('_isMainPlanning', new FormControl(isMainPlanning));
                pheForm.form.controls.phe.addControl('entity_id', new FormControl(mainEntity.id));

                formData.form.controls.mainPlanningHas = pheForm.form;
                formData.mainPhe = pheForm.pheFields;

                const beginDateChange$ = pheForm.form.controls.phe.get('begindate').valueChanges;
                const endDateChange$ = pheForm.form.controls.phe.get('enddate').valueChanges;

                subscriptions.add(
                    beginDateChange$
                        .pipe(startWith(pheForm.form.controls.phe.get('begindate').value), pairwise())
                        .subscribe(([previousBegindate, begindate]) => {
                            if (!previousBegindate) {
                                return;
                            }

                            const mainBegin = new Date(previousBegindate);
                            const dayShift = Utils.dayDiff(mainBegin, begindate);
                            formData.form.controls.planningHas.controls.forEach(pheFg => {
                                if (pheFg !== pheForm.form) {
                                    const pheBegin = new Date(pheFg.get('phe').get('begindate').value);
                                    if (Utils.getTimeOrNull(mainBegin) === Utils.getTimeOrNull(pheBegin)) {
                                        pheFg.get('phe').get('begindate').setValue(new Date(begindate));
                                        pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                    } else if (dayShift) {
                                        pheFg.get('phe').get('begindate').setValue(Utils.addDays(pheBegin, dayShift));
                                        pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                    }
                                }
                            });
                        })
                );
                subscriptions.add(
                    endDateChange$
                        .pipe(startWith(pheForm.form.controls.phe.get('enddate').value), pairwise())
                        .subscribe(([previousEnddate, enddate]) => {
                            if (!previousEnddate) {
                                return;
                            }

                            const mainEnd = new Date(previousEnddate);
                            const dayShift = Utils.dayDiff(mainEnd, enddate);
                            formData.form.controls.planningHas.controls.forEach(pheFg => {
                                if (pheFg !== pheForm.form) {
                                    const pheEnd = new Date(pheFg.get('phe').get('enddate').value);
                                    if (Utils.getTimeOrNull(mainEnd) === Utils.getTimeOrNull(pheEnd)) {
                                        pheFg.get('phe').get('enddate').setValue(new Date(enddate));
                                        pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                    } else if (dayShift) {
                                        pheFg.get('phe').get('enddate').setValue(Utils.addDays(pheEnd, dayShift));
                                        pheFg.get('phe').get('entity_id').updateValueAndValidity();
                                    }
                                }
                            });
                        })
                );
            } else if (formData.mainPhe?.phe) {
                ['begindate', 'enddate'].forEach(dateField => {
                    const control = pheForm.form.controls.phe.get(dateField);
                    const mainField = formData.mainPhe.phe.get(dateField);

                    if (control && mainField) {
                        control.setValue(mainField.fc.value);
                    }
                });
            }
        }

        if (pheForm.form.controls.phe.get('driver_user_id')) {
            subscriptions?.add(
                pheForm.form.controls.phe.get('driver_user_id').valueChanges.subscribe(value => {
                    formData.form.controls.employeePlanning.controls.forEach(employeePlanningControl => {
                        employeePlanningControl.updateValueAndValidity();
                    });
                })
            );
        }

        if (pheForm.form.controls.phe.get('begindate') && pheForm.form.controls.phe.get('enddate')) {
            subscriptions?.add(
                pheForm.form.controls.phe.get('begindate').valueChanges.subscribe(value => {
                    if (value && pheForm.form.controls.phe.get('enddate').value) {
                        const newDate = new Date(value);
                        const newEndDate = new Date(pheForm.form.controls.phe.get('enddate').value);

                        newEndDate.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());

                        setTimeout(() => {
                            if (newEndDate.getTime() > newDate.getTime()) {
                                if (
                                    Utils.getTimeOrNull(pheForm.form.controls.phe.get('enddate').value) !==
                                    Utils.getTimeOrNull(newEndDate)
                                ) {
                                    (pheForm.form.controls.phe.get('enddate') as any as FormControl).setValue(
                                        newEndDate
                                    );
                                }
                            } else {
                                newEndDate.setDate(newDate.getDate() + 1);
                                if (
                                    Utils.getTimeOrNull(pheForm.form.controls.phe.get('enddate').value) !==
                                    Utils.getTimeOrNull(newEndDate)
                                ) {
                                    (pheForm.form.controls.phe.get('enddate') as any as FormControl).setValue(
                                        newEndDate
                                    );
                                }
                            }
                        }, 0);
                    }
                })
            );
        }

        formData.form.controls.planningHas.push(pheForm.form);

        formData.pheFields.push(pheForm.pheFields);

        pheForm.pheFields.form = pheForm.form;

        return pheForm;
    }

    removePheFromForm(formData: PlanningFormData, pheField: PheFormData) {
        const indexToRemove = formData.form.controls.planningHas.value.findIndex(
            ({ phe }) =>
                phe.entity_id === pheField.form.value.phe.entity_id &&
                phe.driver_user_id === pheField.form.value.phe.driver_user_id
        );

        formData.form.controls.planningHas.removeAt(indexToRemove);
        formData.form.controls.planningHas.value.splice(indexToRemove, 1);

        formData.pheFields.splice(indexToRemove, 1);

        const orginalIndex = formData.orginal?.planning_has?.findIndex(ph => ph.id === pheField.id);

        if (orginalIndex >= 0) {
            formData.orginal.planning_has.splice(orginalIndex, 1);
        }

        formData.form.controls.planningHas.updateValueAndValidity();
        formData.form.controls.planningHas.markAsDirty();

        if (pheField.form.value.phe.driver_user_id) {
            formData.form.controls.employeePlanning.controls.forEach(employeePlanningControl => {
                employeePlanningControl.updateValueAndValidity();
            });
        }
    }

    private watchPerformerFields(subscriptions: Subscription, formData: PlanningFormData, entityType: EntityType) {
        ['performer', 'asphalt_performer'].forEach(field => {
            if (formData.form.controls.planning.get(field)) {
                this.performerChange(subscriptions, formData, entityType, field);
            }
        });
    }

    private performerChange(
        subscriptions: Subscription,
        formData: PlanningFormData,
        entityType: EntityType,
        field = 'performer'
    ) {
        subscriptions.add(
            combineLatest([
                formData.form.controls.planning
                    .get(field)
                    .valueChanges.pipe(startWith(formData.form.controls.planning.get(field).value), pairwise()),
                this.entitiesService.getByType(entityType.has_entitytype_keys ?? []),
                this.userService.getList(false, false),
            ]).subscribe(([[previousPerformerId, newPerformerId], entities, employees]) => {
                if (previousPerformerId === newPerformerId) {
                    return;
                }

                // Clean up previous performer's PHE if it exists
                if (previousPerformerId) {
                    const previousDriverEntity = entities.find(e => e.driver_user_id === previousPerformerId);
                    if (previousDriverEntity) {
                        const pheToRemove = formData.pheFields.find(
                            phe =>
                                phe.form.value.phe.entity_id === previousDriverEntity.id &&
                                phe.form.value.phe.driver_user_id === previousPerformerId
                        );
                        if (pheToRemove) {
                            this.removePheFromForm(formData, pheToRemove);
                        }
                    }

                    // De voorman zelf is hierboven al verwijderd, maar zijn standaardpersoneel (dat
                    // net zo goed chauffeur kan zijn en dus als planning_has staat) moet ook weg.
                    this.removeDefaultEmployees(formData, [previousPerformerId], employees);
                }

                // Handle new performer
                if (newPerformerId) {
                    this.addDriverEntityToPlanning(formData, entityType, newPerformerId, entities, subscriptions);
                }

                this.syncDefaultEmployees(formData, entityType, employees, entities, subscriptions);
            })
        );
    }

    getDriverEntity(userId: number, entityType: EntityType, entities: Entity[]) {
        const hasEntitytypeKeys = entityType.has_entitytype_keys ?? [];
        const entity = entities.find(
            item =>
                item.driver_user_id === userId &&
                item.entitytypes.some(itemEntityType => hasEntitytypeKeys.includes(itemEntityType.key))
        );
        const entityTypeKey = entity?.entitytypes.find(itemEntityType =>
            hasEntitytypeKeys.includes(itemEntityType.key)
        )?.key;

        if (!entity || !entityTypeKey) {
            return null;
        }

        return { entity, entityTypeKey };
    }

    addDriverEntityToPlanning(
        formData: PlanningFormData,
        entityType: EntityType,
        userId: number,
        entities: Entity[],
        subscriptions: Subscription
    ): boolean {
        const driverEntity = this.getDriverEntity(userId, entityType, entities);

        if (!driverEntity) {
            return false;
        }
        const { entity: driverFrom, entityTypeKey } = driverEntity;

        const existingPheWithDriver = formData.pheFields.some(phe => phe.form.value.phe.driver_user_id === userId);

        if (existingPheWithDriver) {
            return true;
        }

        const existingPheWithoutDriver = formData.pheFields.find(
            phe => phe.form.value.phe.entity_id === driverFrom.id && !phe.form.value.phe.driver_user_id
        );

        if (existingPheWithoutDriver) {
            (existingPheWithoutDriver.form.controls.phe.get('driver_user_id') as unknown as FormControl).setValue(
                userId
            );

            return true;
        }

        // de entitytypes op een entity komen van de api en bevatten geen fields_*,
        // daarom het volledige entitytype uit de environment pakken
        const driverEntityType = environment.entitytypes[entityTypeKey];

        if (!driverEntityType) {
            return false;
        }

        const phe = this.addPheToForm(formData, driverEntityType, undefined, false, undefined, subscriptions);

        (phe.form.controls.phe.get('driver_user_id') as unknown as FormControl).setValue(userId);
        (phe.form.controls.phe.get('entity_id') as unknown as FormControl).setValue(driverFrom.id);

        const truckControl = phe.form.controls.phe.get('truck_entity_id');
        if (driverFrom.truck_entity_id && truckControl) {
            (truckControl as unknown as FormControl).setValue(driverFrom.truck_entity_id);
        }

        const lowloaderControl = phe.form.controls.phe.get('lowloader_entity_id');
        if (driverFrom.lowloader_entity_id && lowloaderControl) {
            (lowloaderControl as unknown as FormControl).setValue(driverFrom.lowloader_entity_id);
        }

        return true;
    }

    private watchStatusForDefaultEmployees(
        subscriptions: Subscription,
        formData: PlanningFormData,
        entityType: EntityType
    ) {
        const statusControl = formData.form.controls.planning.get('status_key');

        if (!statusControl) {
            return;
        }

        subscriptions.add(
            statusControl.valueChanges.subscribe(newStatusId => {
                if (
                    formData.form.controls.planning.value['status_key'] !== newStatusId &&
                    newStatusId === environment.status.Planning.PlanningDefinitief
                ) {
                    this.applyDefaultEmployees(formData, entityType, subscriptions);
                }
            })
        );
    }

    // Voegt het standaardpersoneel van de voorman/entiteit toe aan de planning. Een standaardmedewerker
    // die zelf chauffeur is (driver_user_id op een entity) komt, net als de voorman zelf, als
    // planning_has_entity op de planning in plaats van als losse user_planning.
    applyDefaultEmployees(formData: PlanningFormData, entityType: EntityType, subscriptions: Subscription) {
        subscriptions.add(
            combineLatest([
                this.userService.getList(false, false),
                this.entitiesService.getByType(entityType.has_entitytype_keys ?? []),
            ])
                .pipe(first())
                .subscribe(([employees, entities]) => {
                    this.syncDefaultEmployees(formData, entityType, employees, entities, subscriptions);
                })
        );
    }

    private syncDefaultEmployees(
        formData: PlanningFormData,
        entityType: EntityType,
        employees: User[],
        entities: Entity[],
        subscriptions: Subscription
    ) {
        const performerIds = ['performer', 'asphalt_performer']
            .map(field => formData.form.controls.planning?.get(field)?.value as number)
            .filter(id => !!id);

        const inEmployeePlanning = formData.form.controls.employeePlanning.value.map(c => c.user_id);
        const inPlanningHas = formData.form.controls.planningHas.controls.map(fg => fg.value.phe?.driver_user_id);

        this.employeeService
            .getDefaultEmployees(employees, formData.mainEntity?.default_employees ?? [], performerIds)
            .filter(
                e =>
                    !inEmployeePlanning.includes(e.id) &&
                    !inPlanningHas.includes(e.id) &&
                    employees.some(emp => emp.id === e.id)
            )
            .forEach(user => {
                const addedAsDriver = this.addDriverEntityToPlanning(
                    formData,
                    entityType,
                    user.id,
                    entities,
                    subscriptions
                );

                if (!addedAsDriver) {
                    this.addEmployeeToPlanning(formData, user.id, employees, subscriptions);
                }
            });
    }

    private removeDefaultEmployees(formData: PlanningFormData, performerIds: number[], employees: User[]) {
        this.employeeService.getDefaultEmployees(employees, [], performerIds).forEach(user => {
            const fgEmployee = formData.form.controls.employeePlanning.controls.find(c => c.value.user_id === user.id);

            if (fgEmployee) {
                this.removeEmployeeFromPlanning(formData, fgEmployee);
            }

            const pheField = formData.pheFields.find(phe => phe.form.value.phe.driver_user_id === user.id);

            if (pheField) {
                this.removePheFromForm(formData, pheField);
            }
        });
    }

    private addEmployeeToPlanning(
        formData: PlanningFormData,
        userId: number,
        employees: User[],
        subscriptions: Subscription
    ) {
        const fg = this.createEmployeeFormGroup(formData, userId, employees, subscriptions);

        formData.form.controls.employeePlanning.push(fg);
        formData.form.markAsDirty();
        formData.form.updateValueAndValidity();

        return fg;
    }

    private removeEmployeeFromPlanning(formData: PlanningFormData, fgEmployee: FormGroup<EmployeePlanningFg>) {
        const index = formData.form.controls.employeePlanning.controls.indexOf(fgEmployee);

        if (index === -1) {
            return;
        }

        formData.form.controls.employeePlanning.removeAt(index);
        formData.form.controls.employeePlanning.value.splice(index, 1);
        formData.form.controls.employeePlanning.updateValueAndValidity();

        formData.form.controls.planningHas.controls.forEach(fg => {
            if (fg.controls.phe.get('driver_user_id')) {
                fg.controls.phe.get('driver_user_id').updateValueAndValidity();
            }
        });

        formData.form.markAsDirty();
    }

    createEmployeeFormGroup(
        formData: PlanningFormData,
        userId: number,
        employees: User[],
        subscriptions: Subscription,
        defaults: Partial<{
            id: number;
            _moved_from_id: number;
            planning_id: number;
            begindate: Date;
            enddate: Date;
            comment: string;
            updated_at: Date;
            origin: string;
            destination: string;
            work_begin: string;
            work_end: string;
        }> = {},
        movedFrom?: UserPlanning
    ): FormGroup<EmployeePlanningFg> {
        const phe = formData.form.controls.mainPlanningHas.controls.phe;
        const employee = employees.find(e => e.id === userId);
        const address = this.locationService.formatAddress(environment.companyAddress);
        const location = formData.form.controls.planning?.get('location')?.value;

        const fg = new FormGroup<EmployeePlanningFg>(
            {
                id: new FormControl(defaults.id ?? null),
                _moved_from_id: new FormControl(defaults._moved_from_id ?? null),
                user_id: new FormControl(userId),
                planning_id: new FormControl(defaults.planning_id ?? formData.orginal?.id),
                begindate: new FormControl(defaults.begindate ?? phe.get('begindate').value, Validators.required),
                enddate: new FormControl(defaults.enddate ?? phe.get('enddate').value, Validators.required),
                conflictText: new FormControl(''),
                comment: new FormControl(defaults.comment ?? null),
                function: new FormControl(employee?.function),
                employee: new FormControl(employee?.name),
                updated_at: new FormControl(defaults.updated_at ?? null),
                origin: new FormControl(defaults.origin ?? address),
                destination: new FormControl(defaults.destination ?? address),
                work_begin: new FormControl(defaults.work_begin ?? location),
                work_end: new FormControl(defaults.work_end ?? location),
            },
            [],
            localEmployeeAvailableValidator(
                this.userPlanningService,
                this.planningHasService,
                movedFrom ?? null,
                formData?.orginal?.id,
                () =>
                    Array.from(formData.form.controls.planningHas?.getRawValue()?.map(ph => ph.phe) ?? []) as Pick<
                        PlanningHasEntity,
                        'id' | 'driver_user_id' | 'begindate' | 'enddate'
                    >[],
                () => formData.form?.controls?.employeePlanning?.controls ?? []
            )
        );

        subscriptions.add(
            fg.controls.begindate.valueChanges.subscribe(() => {
                fg.controls.enddate.updateValueAndValidity();
            })
        );

        subscriptions.add(
            fg.controls.enddate.valueChanges.subscribe(enddate => {
                const begindate = new Date(fg.controls.begindate.value);

                if (!enddate) {
                    return;
                }

                enddate = new Date(enddate);

                begindate.setSeconds(0);

                const corrected = new Date(enddate);

                corrected.setFullYear(begindate.getFullYear(), begindate.getMonth(), begindate.getDate());

                if (corrected < begindate) {
                    corrected.setDate(corrected.getDate() + 1);
                }

                if (corrected.getTime() !== enddate.getTime()) {
                    fg.controls.enddate.setValue(corrected, { emitEvent: false, onlySelf: true });
                }
            })
        );

        return fg;
    }

    private createPlanningFormGroup(entityType: EntityType, planning?: Planning) {
        const planningFieldsMap = new Map<string, Map<string, EntityFormField>>();

        const form = new FormGroup<PlanningFg>({
            planning: new FormGroup({}),
            planningData: new FormGroup({}),
            planningHas: new FormArray([]),
            mainPlanningHas: new FormGroup({
                phe: new FormGroup({}),
                pheData: new FormGroup({}),
            }),
            employeePlanning: new FormArray([]),
            planningMaterials: new FormArray([]),
        });

        Object.entries({
            planning: entityType.fields_planning,
            planningData: entityType.fields_planning_data,
        }).forEach(([formGroup, entityData]) => {
            planningFieldsMap.set(formGroup, new Map<string, EntityFormField>());

            if (entityData) {
                Object.entries(entityData).forEach(([fcKey, entityField]) => {
                    const control = new FormControl(entityField.default ?? null);

                    (form.get(formGroup) as FormGroup).addControl(fcKey, control);

                    control.addValidators(this.buildValidators(entityField));

                    // Add async validators
                    const asyncValidators = this.buildAsyncValidators(
                        entityField,
                        form.get(formGroup) as FormGroup,
                        planning
                    );

                    if (asyncValidators.length > 0) {
                        control.addAsyncValidators(asyncValidators);
                    }

                    planningFieldsMap.get(formGroup).set(fcKey, { ...entityField, fc: control });

                    entityField.extraFields?.forEach(hiddenField => {
                        (form.get(formGroup) as FormGroup).addControl(hiddenField, new FormControl());
                    });
                });
            }
        });

        return { form, planningFieldsMap };
    }

    private createPheFormGroup(entityType: EntityType, planning?: Planning) {
        const pheFields = new PheFormData();

        const form = new FormGroup({
            phe: new FormGroup({}),
            pheData: new FormGroup({}),
        });

        Object.entries({
            phe: entityType.fields_phe,
            pheData: entityType.fields_phe_data,
        }).forEach(([formGroup, entityData]) => {
            pheFields[formGroup] = new Map<string, EntityFormField>();

            if (entityData) {
                Object.entries(entityData).forEach(([fcKey, entityField]) => {
                    const control = new FormControl(entityField.default ?? null);

                    (form.get(formGroup) as FormGroup).addControl(fcKey, control);

                    control.addValidators(this.buildValidators(entityField));

                    // Add async validators
                    const asyncValidators = this.buildAsyncValidators(
                        entityField,
                        form.get(formGroup) as FormGroup,
                        planning
                    );

                    if (asyncValidators.length > 0) {
                        control.addAsyncValidators(asyncValidators);
                    }

                    pheFields[formGroup].set(fcKey, { ...entityField, fc: control });

                    entityField.extraFields?.forEach(hiddenField => {
                        (form.get(formGroup) as FormGroup).addControl(hiddenField, new FormControl());
                    });
                });
            }
        });

        return { form, pheFields };
    }

    private buildValidators(field: EntityField): ValidatorFn[] {
        const validators: ValidatorFn[] = [];
        if (field.required) {
            validators.push(Validators.required);
        }
        if (field.validators?.min) {
            validators.push(Validators.min(field.validators.min));
        }
        if (field.validators?.max) {
            validators.push(Validators.max(field.validators.max));
        }
        if (field.validators?.pattern) {
            validators.push(Validators.pattern(field.validators.pattern));
        }
        if (field.validators?.minLength) {
            validators.push(Validators.minLength(field.validators.minLength));
        }
        if (field.validators?.maxLength) {
            validators.push(Validators.maxLength(field.validators.maxLength));
        }

        return validators;
    }

    private buildAsyncValidators(field: EntityField, formGroup: FormGroup, planning?: Planning): AsyncValidatorFn[] {
        const asyncValidators: AsyncValidatorFn[] = [];

        if (field.asyncValidators) {
            field.asyncValidators.forEach(asyncValidatorConfig => {
                switch (asyncValidatorConfig.type) {
                    case 'localUserIsAvailable':
                        asyncValidators.push(
                            localUserIsAvailableValidator({
                                formGroup,
                                planningId: planning?.id,
                                userPlanningService: this.userPlanningService,
                                planningHasService: this.planningHasService,
                                ...asyncValidatorConfig.config,
                            })
                        );
                        break;
                    case 'localEntityIsAvailable':
                        asyncValidators.push(
                            localEntityIsAvailableValidator({
                                formGroup,
                                planningId: planning?.id,
                                planningHasService: this.planningHasService,
                                entitiesService: this.entitiesService,
                                ...asyncValidatorConfig.config,
                            })
                        );
                        break;
                    // Add more async validators here as needed
                    default:
                        console.warn(`Unknown async validator type: ${asyncValidatorConfig.type}`);
                }
            });
        }

        return asyncValidators;
    }

    private buildConditionalValidators(planningForm: PlanningFormData, subscriptions: Subscription): PlanningFormData {
        // planning fixed validator
        const statusControl = planningForm.form.controls.planning.get('status_key');
        const begindateControl = planningForm.form.controls.mainPlanningHas.get('phe').get('begindate');

        const statusList$ = this.statusService.getList('planning').pipe(shareReplay(1));

        const statusValidator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => {
            const statusKey = control.value;
            const begindateValue = begindateControl.value;

            if (!begindateValue || !statusKey) {
                return of(null);
            }
            const begindate = new Date(begindateValue);

            return this.planningFixedService.getLatestFixedUntilItem().pipe(
                take(1),
                switchMap(fixed => {
                    const isFixed = fixed && new Date(fixed.fixeduntil).getTime() >= begindate.getTime();
                    if (!isFixed) {
                        return of(null);
                    }

                    return statusList$.pipe(
                        take(1),
                        map((statuses: Status[]) => {
                            const selectedStatus = statuses.find(s => s.key === statusKey);
                            if (selectedStatus && !selectedStatus.fixed) {
                                return { invalidStatusForFixed: { statusName: selectedStatus.name } };
                            }
                            return null;
                        })
                    );
                })
            );
        };

        statusControl.setAsyncValidators(statusValidator);
        statusControl.updateValueAndValidity({ emitEvent: false });

        subscriptions.add(
            begindateControl.valueChanges.subscribe(() => {
                statusControl.updateValueAndValidity({ emitEvent: false });
            })
        );

        // Check role-based permissions BEFORE setting up disable rules
        // This ensures role-based disabling always takes precedence
        this.checkAndDisableFormBasedOnRoles(planningForm);

        // Set up conditional validators for planning fields
        for (const [group, fields] of planningForm.planningFieldsMap) {
            for (const [key, entityField] of fields) {
                if (entityField.requiredRules) {
                    const control = planningForm.form.get(group).get(key) as FormControl;
                    for (const rule of entityField.requiredRules) {
                        this.setupRule(
                            control,
                            entityField,
                            rule,
                            planningForm.form,
                            ['planning', 'planningData'],
                            subscriptions
                        );
                    }
                }

                if (entityField.disableRules) {
                    for (const rule of entityField.disableRules) {
                        this.setupDisableRule(rule, planningForm, ['planning', 'planningData'], subscriptions);
                    }
                }

                if (entityField.visibleRules) {
                    for (const rule of entityField.visibleRules) {
                        this.setupVisibleRule(
                            entityField,
                            rule,
                            planningForm.form,
                            ['planning', 'planningData'],
                            subscriptions
                        );
                    }
                }

                if (entityField.atLeastOneRequiredRules) {
                    for (const rule of entityField.atLeastOneRequiredRules) {
                        this.setupAtLeastOneRequiredRule(
                            rule,
                            planningForm.form,
                            ['planning', 'planningData'],
                            planningForm.pheFields,
                            subscriptions
                        );
                    }
                }
            }
        }

        // Set up conditional validators for PHE fields
        planningForm.pheFields.forEach(phe => {
            for (const [group, fields] of Object.entries({ phe: phe.phe, pheData: phe.pheData })) {
                for (const [key, entityField] of fields) {
                    if (entityField.requiredRules) {
                        const control = phe.form.get(group).get(key) as FormControl;
                        for (const rule of entityField.requiredRules) {
                            this.setupRule(
                                control,
                                entityField,
                                rule,
                                planningForm.form,
                                ['planning', 'planningData'],
                                subscriptions
                            );
                        }
                    }
                    if (entityField.atLeastOneRequiredRules) {
                        for (const rule of entityField.atLeastOneRequiredRules) {
                            this.setupAtLeastOneRequiredRule(
                                rule,
                                planningForm.form,
                                ['planning', 'planningData'],
                                planningForm.pheFields,
                                subscriptions
                            );
                        }
                    }
                }
            }
        });

        return planningForm;
    }

    private checkAndDisableFormBasedOnRoles(planningForm: PlanningFormData): void {
        if (planningForm.mainPhe?.orginal) {
            const entityType = environment.entitytypes[planningForm.mainPhe.orginal.entitytype_key];
            planningForm.mainPhe.disabledByRoles = !this.userCanEditPlanning(entityType, planningForm.orginal);
            if (planningForm.mainPhe.disabledByRoles) {
                planningForm.mainPhe.form.disable();
                planningForm.form.controls.planning.disable();
                planningForm.form.controls.planningData.disable();
                planningForm.form.disable();
            }
        }
        planningForm.pheFields.forEach(phe => {
            if (phe.orginal?.entitytype_key) {
                const entityType = environment.entitytypes[phe.orginal.entitytype_key];
                phe.disabledByRoles = !this.userCanEditPlanning(entityType, planningForm.orginal);
                if (phe.disabledByRoles) {
                    phe.form.disable();
                }
            }
        });
    }

    userCanEditPlanning(entityType: EntityType, planning?: Planning): boolean {
        // If may_edit_roles is defined and user has one of those roles, allow editing
        if (entityType.may_edit_roles && entityType.may_edit_roles.length > 0) {
            if (AuthorisationService.hasAnyRole(entityType.may_edit_roles)) {
                return true;
            }
        }

        // If may_edit_own_roles is defined, check if user has the role AND is the assigned projectmanager
        if (entityType.may_edit_own_roles && entityType.may_edit_own_roles.length > 0) {
            if (AuthorisationService.hasAnyRole(entityType.may_edit_own_roles)) {
                // Check if this user is the planning's projectmanager
                if (planning && planning.projectmanager === LocalStorage.user.id) {
                    return true;
                }
                // If no planning object (new planning), allow creation
                if (!planning) {
                    return true;
                }
            }
        }

        // If neither role array is defined or empty, allow editing (no restrictions)
        const hasNoEditRoles = !entityType.may_edit_roles || entityType.may_edit_roles.length === 0;
        const hasNoEditOwnRoles = !entityType.may_edit_own_roles || entityType.may_edit_own_roles.length === 0;
        return hasNoEditRoles && hasNoEditOwnRoles;
    }

    setupRule(
        control: FormControl,
        entityField: EntityField,
        rule: { field: string; hasValue: any[] },
        form: FormGroup,
        groups: string[],
        subscriptions: Subscription
    ) {
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }
        if (controllingControl) {
            subscriptions.add(
                controllingControl.valueChanges
                    .pipe(startWith(controllingControl.value), debounceTime(250))
                    .subscribe(value => {
                        const isRequired = rule.hasValue.includes(value);
                        const baseValidators = this.buildValidators(entityField);
                        if (isRequired) {
                            baseValidators.push(Validators.required);
                        }
                        control.setValidators(baseValidators);
                        control.updateValueAndValidity({ emitEvent: false });
                    })
            );
        }
    }

    setupVisibleRule(
        entityField: EntityFormField,
        rule: { field: string; hasValue: any[] },
        form: FormGroup,
        groups: string[],
        subscriptions: Subscription
    ) {
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }

        if (!controllingControl) {
            return;
        }

        const updateVisibility = (value: any) => {
            entityField.hidden = !rule.hasValue.includes(value);
        };

        updateVisibility(controllingControl.value);

        subscriptions.add(controllingControl.valueChanges.pipe(debounceTime(250)).subscribe(updateVisibility));
    }

    setupDisableRule(
        rule: { field: string; hasValue: any[]; disableFields: string[] },
        planningForm: PlanningFormData,
        groups: string[],
        subscriptions: Subscription
    ) {
        const form = planningForm.form;
        const pheFields = planningForm.pheFields;

        // Find the controlling field
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }

        if (controllingControl) {
            // Subscribe to value changes
            subscriptions.add(
                controllingControl.valueChanges.pipe(debounceTime(250)).subscribe(value => {
                    const shouldDisable = rule.hasValue.includes(value);

                    // Disable/enable fields in planning and planningData
                    for (const fieldName of rule.disableFields) {
                        for (const group of groups) {
                            const targetControl = form.get(group)?.get(fieldName);
                            if (targetControl) {
                                if (shouldDisable) {
                                    targetControl.disable();
                                } else if (!planningForm.mainPhe.disabledByRoles) {
                                    targetControl.enable();
                                }
                            }
                        }

                        // Also check PHE fields
                        pheFields.forEach(phe => {
                            const pheControl = phe.form.get('phe')?.get(fieldName);
                            if (pheControl) {
                                if (shouldDisable) {
                                    pheControl.disable();
                                } else if (!phe.disabledByRoles) {
                                    pheControl.enable();
                                }
                            }
                            const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                            if (pheDataControl) {
                                if (shouldDisable) {
                                    pheDataControl.disable();
                                } else if (!phe.disabledByRoles) {
                                    pheDataControl.enable();
                                }
                            }
                        });
                    }
                })
            );

            // Trigger initial state
            const currentValue = controllingControl.value;
            const shouldDisable = rule.hasValue.includes(currentValue);
            for (const fieldName of rule.disableFields) {
                for (const group of groups) {
                    const targetControl = form.get(group)?.get(fieldName);
                    if (targetControl) {
                        if (shouldDisable) {
                            targetControl.disable();
                        } else if (!planningForm.mainPhe.disabledByRoles) {
                            targetControl.enable();
                        }
                    }
                }

                // Also check PHE fields for initial state
                pheFields.forEach(phe => {
                    const pheControl = phe.form.get('phe')?.get(fieldName);
                    if (pheControl) {
                        if (shouldDisable) {
                            pheControl.disable();
                        } else if (!phe.disabledByRoles) {
                            pheControl.enable();
                        }
                    }
                    const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                    if (pheDataControl) {
                        if (shouldDisable) {
                            pheDataControl.disable();
                        } else if (!phe.disabledByRoles) {
                            pheDataControl.enable();
                        }
                    }
                });
            }
        }
    }

    setupAtLeastOneRequiredRule(
        rule: { field: string; hasValue: any[]; requiredFields: string[] },
        form: FormGroup,
        groups: string[],
        pheFields: PheFormData[],
        subscriptions: Subscription
    ) {
        let controllingControl: FormControl | null = null;
        for (const group of groups) {
            const ctrl = form.get(group)?.get(rule.field);
            if (ctrl) {
                controllingControl = ctrl as FormControl;
                break;
            }
        }

        if (!controllingControl) {
            return;
        }

        const requiredControls: { control: AbstractControl; entityField?: EntityField; label: string }[] = [];

        for (const fieldName of rule.requiredFields) {
            const formControl = form.get(fieldName);
            if (formControl) {
                requiredControls.push({ control: formControl, label: fieldName });
            }

            pheFields.forEach(phe => {
                const pheControl = phe.form.get('phe')?.get(fieldName);
                const pheDataControl = phe.form.get('pheData')?.get(fieldName);
                const pheField = phe.phe.get(fieldName);
                const pheDataField = phe.pheData.get(fieldName);

                if (pheControl) {
                    requiredControls.push({
                        control: pheControl,
                        entityField: pheField,
                        label: pheField?.name ?? fieldName,
                    });
                }
                if (pheDataControl) {
                    requiredControls.push({
                        control: pheDataControl,
                        entityField: pheDataField,
                        label: pheDataField?.name ?? fieldName,
                    });
                }
            });
        }

        if (!requiredControls.length) {
            return;
        }

        const fieldLabels = [...new Set(requiredControls.map(({ label }) => label))];

        const atLeastOneValidator: ValidatorFn = (): ValidationErrors | null => {
            if (!rule.hasValue.includes(controllingControl.value)) {
                return null;
            }

            const hasValue = requiredControls.some(({ control }) =>
                control instanceof FormArray
                    ? control.controls.some(childControl => childControl.valid)
                    : !!control.value
            );

            return hasValue ? null : { atLeastOneRequired: { fields: fieldLabels } };
        };

        requiredControls.forEach(({ control, entityField }) => {
            if (entityField) {
                control.setValidators([...this.buildValidators(entityField), atLeastOneValidator]);
            } else {
                control.addValidators(atLeastOneValidator);
            }
            control.updateValueAndValidity({ emitEvent: false });
        });

        const updateRequiredControls = () => {
            requiredControls.forEach(({ control }) => control.updateValueAndValidity({ emitEvent: false }));
        };

        subscriptions.add(controllingControl.valueChanges.subscribe(updateRequiredControls));
        requiredControls.forEach(({ control }) => {
            subscriptions.add(control.valueChanges.subscribe(updateRequiredControls));
        });
    }
}

export interface PlanningFg {
    planning: FormGroup;
    planningData: FormGroup;
    planningHas: FormArray<
        FormGroup<{
            phe: FormGroup;
            pheData: FormGroup;
        }>
    >;
    mainPlanningHas: FormGroup<{
        phe: FormGroup;
        pheData: FormGroup;
    }>; //zit ook in planningHas
    employeePlanning: FormArray<FormGroup<EmployeePlanningFg>>;
    planningMaterials: FormArray;
}

export class PlanningFormData {
    orginal: Planning;
    form: FormGroup<PlanningFg>;
    planningFieldsMap: Map<string, Map<string, EntityFormField>>;
    pheFields: PheFormData[] = [];
    mainPhe: PheFormData;
    mainEntityType: EntityType;
    mainEntity: Entity;
    planningFiles: PlanningFile[] = [];
}

export class PheFormData {
    id: number;
    orginal: PlanningHasEntity;
    form: FormGroup<{
        phe: FormGroup;
        pheData: FormGroup;
    }>;
    phe: Map<string, EntityFormField>;
    pheData: Map<string, EntityFormField>;
    disabledByRoles: boolean = false;
}

export interface EmployeePlanningFg {
    id?: FormControl<number>;
    _moved_from_id?: FormControl<number>;
    afas_project_id?: FormControl<string>;
    dayOff?: FormControl<boolean>;
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    user_id: FormControl<number>;
    planning_id: FormControl<number>;
    origin?: FormControl<string>;
    destination?: FormControl<string>;
    work_begin?: FormControl<string>;
    work_end?: FormControl<string>;
    comment: FormControl<string>;
    created_at?: FormControl<Date>;
    updated_at?: FormControl<Date>;
    deleted_at?: FormControl<Date>;

    conflictText?: FormControl<string>;
    function?: FormControl<string>;
    employee?: FormControl<string>;
}

export interface EntityFormField extends EntityField {
    fc: FormControl;
    hidden?: boolean;
}
